"use client";

import { useEffect, useMemo } from "react";
import { parseISO } from "date-fns";
import { getMultiUnitGridABTestingVariant, MultiUnitGridABTestVariants } from "@/app/src/hooks/useMultiUnitGridABTesting";
import { getSearchPviAlgoABTestVariant } from "@/app/src/hooks/usePVIABTesting";
import { SORT_KEY_VALUE_MAPPING } from "@/constant";
import { consumeLocationSearchTracking } from "components/searchBar/location-search/tracking";
import { useAppSelector } from "stores/hooks";
import { selectSearchAddressResult } from "stores/save-search/saveSearchSlice";
import { selectSearchQuery } from "stores/search/searchSlice";
import { diffDays } from "utils/dates";
import { canUseDOM } from "utils/dom";
import { getAddressComponents } from "utils/helper";
import { getFiltersInLocalStorage } from "utils/local-storage";
const SearchAnalytics = ({
  expandedPropertiesReturned,
  expandedRadiusMiles,
  count,
  withPolygon,
  multiUnitPropertyCount,
  totalHostBadgeCount
}: {
  expandedPropertiesReturned?: number | null;
  expandedRadiusMiles?: number | null;
  count?: number;
  withPolygon?: boolean;
  multiUnitPropertyCount?: number | null;
  totalHostBadgeCount?: number | null;
}) => {
  const searchQuery = useAppSelector(selectSearchQuery);
  const searchAddress = useAppSelector(selectSearchAddressResult);
  const searchCityStateCountry = useMemo(() => {
    return getAddressComponents(searchAddress?.address_components || []);
  }, [searchAddress]);

  // const lastSearchQuery = useRef(searchQuery?.name)
  // const hasTracked = useRef(false)

  useEffect(() => {
    // Skip if no search query
    if (!searchQuery?.name) {
      return;
    }

    // // Skip if we've already tracked this exact search query
    // if (hasTracked.current && lastSearchQuery.current === searchQuery.name) {
    //     return;
    // }

    // // Update refs before running analytics
    // hasTracked.current = true;
    // lastSearchQuery.current = searchQuery.name;

    setTimeout(() => {
      const pageOrigin = localStorage.getItem("searchPageOrigin");
      localStorage.removeItem("searchPageOrigin");
      const {
        locationInputMethod,
        searchSurface
      } = consumeLocationSearchTracking();
      const searchPviAlgoVariant = getSearchPviAlgoABTestVariant();
      const fullFilterList = (JSON.parse(getFiltersInLocalStorage()) as {
        featureCategory: string;
        id: number;
        keyName: string;
      }[]) || [];
      const selectedFilters = [...(searchQuery.FeaturesAndAmenities?.split(",") || []), ...(searchQuery.Location?.split(",") || [])].map(id => (fullFilterList?.find(filterItem => filterItem.id === +id)?.keyName as string)).filter(x => !!x) || [];
      if (searchQuery?.petAllowed) {
        selectedFilters?.push("Pets allowed");
      }
      if (searchQuery?.partyAllowed || searchQuery?.eventAllowed) {
        selectedFilters?.push("Events allowed");
      }
      if (searchQuery?.childAllowed || searchQuery.childrenCount && +searchQuery.childrenCount > 0) {
        selectedFilters?.push("Children welcome");
      }
      if (searchQuery?.smokeAllowed) {
        selectedFilters?.push("Smoking allowed");
      }
      if (searchQuery?.cancellable) {
        selectedFilters?.push("Cancellation allowed");
      }
      let locationNameFromUrl = "";
      if (canUseDOM()) {
        const search = window.location.search;
        const searchParams = new URLSearchParams(search);
        locationNameFromUrl = (searchParams.get("name") as string) || searchQuery?.name?.replace(/--/g, ", ")?.replace(/-/g, " ") || "NA";
      }
      void (async () => {
        const mixpanelV2Client = (await import("services/mixpanelv2")).mixpanelV2Client;
        mixpanelV2Client.VIEW_SEARCH_RESULTS_PAGE({
          terms: locationNameFromUrl,
          city: searchCityStateCountry.city,
          state: searchCityStateCountry.state,
          country: searchCityStateCountry.country,
          dates: searchQuery.startDate || searchQuery.endDate ? `${searchQuery.startDate || ""}, ${searchQuery.endDate || ""}` : "NA",
          numberOfDaysBeforeCheckin: searchQuery.startDate ? diffDays(parseISO(new Date().toJSON().slice(0, 10)), new Date(searchQuery.startDate)) : 0,
          shouldDisplayTotalPrice: !!searchQuery.toggleOn,
          totalNights: searchQuery.startDate && searchQuery.endDate && searchQuery.noOfDays ? searchQuery.noOfDays : "NA",
          noOfGuest: (searchQuery.adultCount || 0) + (searchQuery.childrenCount || 0) || "NA",
          totalProperty: count || 0,
          numberOfBedroom: searchQuery.minBedRoom || "NA",
          numberOfBeds: searchQuery.minBed || "NA",
          numberOfBathroom: searchQuery.minBathRoom || "NA",
          minPrice: searchQuery.minPrice || "NA",
          maxPrice: searchQuery.maxPrice == 1001 ? "NA" : searchQuery.maxPrice || "NA",
          filters: selectedFilters.join(",") || "NA",
          methodOfSearch: searchQuery?.mapDragged ? "Map" : "Defined Location",
          locationName: searchQuery?.name?.replace(/--/g, ", ")?.replace(/-/g, " ") || "",
          sortBy: SORT_KEY_VALUE_MAPPING[(searchQuery.sortBy as keyof typeof SORT_KEY_VALUE_MAPPING) || "recommended"],
          pageOrigin: pageOrigin ? pageOrigin : searchQuery?.mapDragged ? "open via link - map" : "open via link",
          locationInputMethod,
          searchSurface,
          lat: +searchQuery.lat,
          lng: +searchQuery.lng,
          bounds: searchQuery.bounds,
          isPolygonUsed: withPolygon,
          multiUnitPropertyCount: multiUnitPropertyCount ?? "NA",
          expandedPropertiesReturned: expandedPropertiesReturned ?? 0,
          expandedRadiusMiles: expandedRadiusMiles ?? 0,
          pviVersion: searchPviAlgoVariant,
          multiUnitGridABTestVariant: getMultiUnitGridABTestingVariant() === MultiUnitGridABTestVariants.MULTI_UNIT ? "old" : "new",
          theme: "new",
          preferredHostProperties: totalHostBadgeCount || 0
        });
        const mixpanelV1Client = (await import("services/mixpanel")).mixpanelV1Client;
        mixpanelV1Client.SEARCH_PAGE_RESULT({
          city: searchQuery?.mapDragged ? "map area" : searchCityStateCountry.city,
          state: searchCityStateCountry.state,
          propertyCount: count,
          noOfBedrooms: searchQuery?.numberOfBedroom,
          noOfBathrooms: searchQuery?.numberOfBathroom,
          filters: selectedFilters.join(",") || "NA",
          latitude: searchQuery.lat,
          longitude: searchQuery.lng,
          sortBy: SORT_KEY_VALUE_MAPPING[(searchQuery.sortBy as keyof typeof SORT_KEY_VALUE_MAPPING) || "recommended"]
        });
      })();
    }, 500);
  }, [searchQuery?.name]); // do not add other dependencies here, as it will cause multiple event triggers
  return null;
};
export default SearchAnalytics;